// Sheet — one component, two geometries (the spec's panelPos / tallPanelPos),
// each resolving differently on phone (bottom sheet) and board (centred modal).
//
// Adds the dialog semantics the spec lacks: role/aria-modal, focus trap and
// restore, Escape to close, scrim click to dismiss.

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useT } from '@/i18n/useT';
import { useStore } from '@/store';
import { isTablet } from '@/store/slices/ui';

export type SheetVariant = 'panel' | 'tall';
/** Which screen edge a phone sheet hangs from. A board modal ignores it. */
export type SheetSide = 'bottom' | 'top';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  variant?: SheetVariant;
  /**
   * Phone only: a `panel` can hang from the top of the screen instead of
   * rising from the bottom. For something held up to be looked through — the
   * camera viewfinder — the top is where the eyes and the label already are,
   * and a bottom sheet put the picture under the operator's own hands. The
   * grab handle moves to the sheet's free edge with it.
   */
  side?: SheetSide;
  /** Accessible name for the dialog. */
  title: string;
  /** Hide the visible title (when the body renders its own header). */
  hideTitle?: boolean;
  children: ReactNode;
  footer?: ReactNode;
}

export function Sheet({
  open,
  onClose,
  variant = 'panel',
  side = 'bottom',
  title,
  hideTitle = false,
  children,
  footer,
}: SheetProps) {
  const t = useT();
  const vw = useStore((s) => s.vw);
  const board = isTablet(vw);
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  /**
   * The latest `onClose`, without it being a dependency.
   *
   * The trap below used to depend on `onClose`, and every caller writes it
   * inline — `onClose={() => setOpen(false)}` — so it is a new function on
   * every render of the screen holding the sheet. The andon thread ticks:
   * it re-renders once a second for its clock, so the trap tore down and
   * rebuilt itself every second for as long as a sheet was open over it, and
   * each rebuild refocused the autofocus field — which scrolled the close
   * sheet back to its top under somebody's thumb. A ref rather than asking
   * every call site for `useCallback`: the sheet should not be breakable from
   * outside by an ordinary inline handler.
   */
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    // A sheet whose whole purpose is a search box should open with the caret in
    // it — one tap saved, and on a tablet the keyboard is already up. The panel
    // itself takes focus otherwise, so the dialog still starts inside its trap.
    // This has to happen here rather than through React's autoFocus: the
    // panel's own focus() runs in this effect and would take it straight back.
    (panel?.querySelector<HTMLElement>('[data-autofocus]') ?? panel)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      // Only on the way out. Restoring focus while the sheet is still open is
      // what put the caret back on the button behind the scrim.
      restoreRef.current?.focus?.();
    };
    // OPEN ALONE. Anything else here re-runs the trap under somebody's
    // fingers — see `closeRef` above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const geometry: React.CSSProperties = board
    ? variant === 'tall'
      ? {
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(620px, 92%)',
          height: 'min(620px, 88%)',
          borderRadius: 20,
        }
      : {
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(560px, 92%)',
          maxHeight: '88%',
          borderRadius: 20,
          overflowY: 'auto',
        }
    : variant === 'tall'
      ? {
          left: 0,
          right: 0,
          bottom: 0,
          top: 'max(64px, calc(env(safe-area-inset-top) + 32px))',
          borderRadius: '26px 26px 0 0',
        }
      : side === 'top'
        ? {
            left: 0,
            right: 0,
            top: 0,
            maxHeight: '90%',
            borderRadius: '0 0 26px 26px',
            overflowY: 'auto',
            paddingTop: 'env(safe-area-inset-top)',
          }
        : { left: 0, right: 0, bottom: 0, maxHeight: '90%', borderRadius: '26px 26px 0 0', overflowY: 'auto' };
  const fromTop = !board && side === 'top';

  // 44px tall, edge to edge — the handle is the dismiss control, so it has to
  // be hittable with a gloved thumb. Hung from the top it sits on the sheet's
  // bottom edge, which is the edge a thumb reaches for to push it away.
  const handle = (
    <button
      type="button"
      onClick={onClose}
      aria-label={t.dismiss}
      style={{
        display: 'block',
        width: 'calc(100% + 44px)',
        padding: '20px 0',
        margin: fromTop ? '2px -22px -14px' : '-14px -22px 2px',
        background: 'none',
        border: 0,
        cursor: 'pointer',
        flex: 'none',
      }}
    >
      <span className="grabHandle" style={{ display: 'block', marginBottom: 0 }} />
    </button>
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 30,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(8,10,14,.5)', animation: 'ipFade .2s' }}
      />
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: board ? '100%' : 460,
          pointerEvents: 'none',
        }}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          style={{
            position: 'absolute',
            pointerEvents: 'auto',
            background: 'var(--bg)',
            color: 'var(--ink)',
            zIndex: 31,
            display: 'flex',
            flexDirection: 'column',
            animation: board
              ? 'ipModalIn .26s cubic-bezier(.2,.7,.3,1)'
              : fromTop
                ? 'ipDown .32s cubic-bezier(.2,.7,.3,1)'
                : 'ipUp .32s cubic-bezier(.2,.7,.3,1)',
            ...geometry,
          }}
        >
          <div style={{ padding: '14px 22px 0', flex: 'none' }}>
            {!board && !fromTop && handle}
            <h2
              id={titleId}
              className={hideTitle ? 'sr-only' : undefined}
              style={
                hideTitle
                  ? undefined
                  : { margin: '0 0 12px', font: "700 19px 'Space Grotesk',sans-serif", color: 'var(--ink)' }
              }
            >
              {title}
            </h2>
          </div>
          <div style={{ padding: '0 22px 22px', overflowY: 'auto', flex: 1 }}>{children}</div>
          {footer && (
            <div
              style={{
                padding: '12px 22px 20px',
                borderTop: '1px solid var(--line)',
                background: 'var(--bg)',
                flex: 'none',
              }}
            >
              {footer}
            </div>
          )}
          {fromTop && <div style={{ padding: '0 22px 14px', flex: 'none' }}>{handle}</div>}
        </div>
      </div>
    </div>
  );
}
